import { ChangeDetectionStrategy, Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import {
  BehaviorSubject,
  combineLatest,
  Observable,
  Subscription,
  distinctUntilChanged,
  map,
  startWith,
  take,
} from 'rxjs';
import { Store } from '@ngrx/store';
import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';

import {
  CommonBookingFeatureState,
  OrderActions,
  orderData,
  OrderPartStatus,
  QueueService,
  SeatMapActions,
  ServicesActions,
  serviceSelectionsForFragment,
  updatingOrderPart,
} from '@fcom/common-booking';
import {
  RemovedServiceInfo,
  RemovedServiceProgress,
  SelectedServiceData,
  SelectionsPerTraveler,
  TravelerService,
} from '@fcom/dapi/interfaces';
import { Category, FinnairOrder, FinnairServiceItem } from '@fcom/dapi/api/models';
import { GtmService } from '@fcom/common/gtm';
import { ButtonPosition, LoaderTheme } from '@fcom/ui-components';
import { finShare, snapshot } from '@fcom/rx';
import { ElementActions, ElementTypes, FragmentService, GaContext, GtmEvent, GtmPurchaseFlow } from '@fcom/common';
import { setUpsell } from '@fcom/common-booking/store/actions/services.actions';
import { getServicesToShow, isBoundBasedCategory } from '@fcom/common-booking/modules/ancillaries/utils';
import { CmsArticle } from '@fcom/core-api';
import { compareAsJson, unsubscribe } from '@fcom/core/utils';
import { getGA4Items } from '@fcom/common/utils/gtm.utils';
import { FeedbackService } from '@fcom/common/services/feedback/feedback.service';
import { QualtricsSurveys } from '@fcom/common/qualtrics/qualtrics.utils';
import { ConfigService } from '@fcom/core';
import { getServiceData } from '@fcom/common-booking/utils/ancillaries.utils';
import { isFlight } from '@fcom/common-booking/utils/common-booking.utils';
import { LoginStatus } from '@fcom/core-api/login';

import { setStage } from '../../../store/actions/state.actions';
import { MmbStage } from '../../../enums';
import { ManageBookingService } from '../../../services';

@Component({
  selector: 'fin-mmb-review-route',
  templateUrl: './review-route.component.html',
  styleUrls: ['./review-route.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingReviewRouteComponent implements OnInit, OnDestroy {
  readonly ButtonPosition = ButtonPosition;
  readonly LoginStatus = LoginStatus;
  readonly LoaderTheme = LoaderTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @HostBinding('class') classAttribute = 'col-f md-col-3-10';

  order$: Observable<FinnairOrder>;
  services$: Observable<FinnairServiceItem[]>;
  reviewForm: UntypedFormGroup;
  formValid$: Observable<boolean>;
  termAndCondition$: Observable<CmsArticle>;
  subscriptions: Subscription = new Subscription();
  disableTermsAndConditionCheckbox: boolean;
  priceUpdating$: Observable<boolean>;
  cartIsEmpty$: Observable<boolean>;
  serviceRemoveProgressInfo$: BehaviorSubject<RemovedServiceProgress> = new BehaviorSubject({
    removeInProgress: false,
  });
  showLoader$: Observable<boolean>;

  constructor(
    private store$: Store<CommonBookingFeatureState>,
    private gtmService: GtmService,
    private fb: UntypedFormBuilder,
    private fragmentService: FragmentService,
    private paymentQueueService: QueueService,
    private feedbackService: FeedbackService,
    private configService: ConfigService,
    private manageBookingService: ManageBookingService
  ) {}

  ngOnInit(): void {
    this.disableTermsAndConditionCheckbox = this.configService.cfg.disableTermsAndConditionCheckbox;

    this.priceUpdating$ = this.store$.pipe(
      updatingOrderPart(),
      map(
        (statuses) =>
          !!statuses &&
          Object.keys(statuses).some((category: Category) => statuses[category] === OrderPartStatus.UPDATING)
      ),
      startWith(false),
      finShare()
    );

    this.paymentQueueService.cancelOngoingPayment();
    this.order$ = this.store$.pipe(orderData(), finShare());
    this.cartIsEmpty$ = this.order$.pipe(
      map((o) => (o.services.unpaid ?? []).length === 0),
      finShare()
    );

    this.subscriptions.add(
      this.order$.pipe(distinctUntilChanged(compareAsJson)).subscribe((order) => {
        this.store$.dispatch(
          OrderActions.setOrderFetchInformation({ orderInformation: { id: order.id, hash: order.hash } })
        );
        if (this.disableTermsAndConditionCheckbox) {
          this.acceptTerms(true);
        }
        this.gtmService.ecommerceEventGA4(GtmEvent.VIEW_CART, getGA4Items(order, GtmPurchaseFlow.MANAGE_BOOKING, true));
      })
    );
    this.services$ = this.order$.pipe(
      map((booking) => getServicesToShow(booking.services, 'unpaid', false, true)),
      finShare()
    );
    this.reviewForm = this.fb.group({
      confirm: [this.disableTermsAndConditionCheckbox, Validators.requiredTrue],
    });

    this.formValid$ = this.reviewForm.statusChanges.pipe(
      map(() => this.reviewForm.valid),
      startWith(this.reviewForm.valid)
    );

    this.termAndCondition$ = this.fragmentService.getFragment<CmsArticle>(
      'MMB.fragments.ancillaryTermsAndConditions.url'
    );
    this.feedbackService.setSurvey(QualtricsSurveys.MMB_FEEDBACK);
    this.feedbackService.setFeedbackWidgetVisibility(true);
    this.showLoader$ = combineLatest([
      this.priceUpdating$,
      this.serviceRemoveProgressInfo$.pipe(map((info) => info.removeInProgress)),
    ]).pipe(map(([isPriceUpdating, isRemoveInProgress]) => isPriceUpdating && !isRemoveInProgress));
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  modifyClicked(serviceData: SelectedServiceData): void {
    this.gtmService.trackElement(
      'review_modify',
      GaContext.MANAGE_BOOKING,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK,
      `recLoc-${snapshot(this.order$).otherInformation.analyticsToken}`
    );
    this.store$.dispatch(setStage({ stage: MmbStage.VIEW }));
    this.store$.dispatch(setUpsell({ category: serviceData.category, fragmentId: serviceData.fragmentId }));
  }

  payOrAdd(cartIsEmpty: boolean): void {
    if (cartIsEmpty) {
      this.store$.dispatch(setStage({ stage: MmbStage.VIEW }));
    } else {
      this.store$.dispatch(setStage({ stage: MmbStage.CHECKOUT }));
    }

    const element = cartIsEmpty ? 'continue-to-travel-extras' : 'continue-to-checkout';

    this.gtmService.trackElement(
      element,
      GaContext.MANAGE_BOOKING,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );
  }

  acceptTerms(accept: boolean): void {
    this.store$.dispatch(OrderActions.acceptTerms({ accept: !!accept }));
  }

  removeProduct(serviceInfo: RemovedServiceInfo): void {
    const fragmentId = isBoundBasedCategory(serviceInfo.category) ? serviceInfo.boundId : serviceInfo.fragmentId;
    if (serviceInfo.category === Category.SEAT) {
      this.subscriptions.add(
        this.order$.pipe(take(1)).subscribe((order) => {
          this.store$.dispatch(
            SeatMapActions.setSeatForFlight({
              flightId: serviceInfo.fragmentId,
              travelerId: serviceInfo.travelerId,
              seat: {},
            })
          );
          this.removeService(order, serviceInfo);
        })
      );
    } else {
      this.subscriptions.add(
        combineLatest([
          this.store$.pipe(serviceSelectionsForFragment<TravelerService[]>(fragmentId, serviceInfo.category)),
          this.order$,
        ])
          .pipe(take(1))
          .subscribe(([storeSelections, order]) => {
            this.store$.dispatch(
              ServicesActions.setSelectionsForFragment({
                category: serviceInfo.category,
                fragmentId: fragmentId,
                selections: this.createNewSelections(serviceInfo, storeSelections),
              })
            );
            this.removeService(order, serviceInfo);
          })
      );
    }
  }

  private createNewSelections(
    serviceInfo: RemovedServiceInfo,
    storeSelections: SelectionsPerTraveler<TravelerService[]>
  ): SelectionsPerTraveler<TravelerService[]> {
    if (storeSelections[serviceInfo.travelerId].length) {
      return {
        ...storeSelections,
        [serviceInfo.travelerId]: storeSelections[serviceInfo.travelerId].filter(
          (selection) => selection.variant !== serviceInfo.variant
        ),
      };
    } else {
      const newSelections = {};
      for (const key of Object.keys(storeSelections)) {
        if (key !== serviceInfo.travelerId) {
          newSelections[key] = storeSelections[key];
        }
      }
      return newSelections;
    }
  }

  private removeService(order: FinnairOrder, serviceInfo: RemovedServiceInfo): void {
    this.serviceRemoveProgressInfo$.next({ service: serviceInfo, removeInProgress: true });
    const allFlights = order.bounds.flatMap((b) => b.itinerary).filter(isFlight);
    const [requests] = getServiceData(this.store$, serviceInfo.category, order.bounds, allFlights);
    this.manageBookingService.setServices(requests, serviceInfo.category, {
      next: () => {
        this.serviceRemoveProgressInfo$.next({ removeInProgress: false });
      },
      error: () => {
        this.serviceRemoveProgressInfo$.next({ removeInProgress: false });
      },
    });
  }
}
